/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import Select from "react-select";
import { FormGroup, FormText, Label } from "reactstrap";
import { RadioButtons } from "./RadioButton";
import { useManagedOptionsHook } from "./useManagedOptionsHook";

interface SharedProps {
    setValue: (name: string, value: any) => void;
    object: Record<string, any>;
    name: string;
    label: string;
    disabled?: boolean;
    options_name?: string | false;
    description?: string;
    extra_context?: Record<string, any>;
}

export interface SelectManagedProps extends SharedProps {
    post_value: string | string[] | null;
    multi?: boolean;
    hide_when_no_options?: boolean;
    return_objects?: boolean;
    return_array?: boolean;
    placeholder?: string;
}

// * use return_objects if you want to return the array with objects
// * use return array if you want to return the array with values
// * by default will return a comma seperated string, to be stored as meta value as string
export const SelectManaged = ({
    setValue,
    object,
    post_value,
    name,
    label,
    disabled,
    multi,
    options_name,
    hide_when_no_options,
    return_objects,
    return_array,
    description,
    extra_context = {},
    placeholder = undefined
}: SelectManagedProps) => {
    const options = useManagedOptionsHook({ object: { ...object, ...extra_context }, name: options_name || name });

    const selectedValue = useMemo(() => {
        if (return_objects && post_value) return post_value;

        if (multi) {
            let selectedOptions;

            if (return_array && post_value) {
                selectedOptions = post_value ?? [];
            } else {
                selectedOptions = post_value && post_value.length > 0 ? String(post_value).split(",") : [];
            }

            return options.filter((i) => selectedOptions.includes(i.value));
        }

        return options.find((i) => String(i.value) === String(post_value)) || {};
    }, [return_objects, post_value, multi, return_array, options]);

    const onChange = (option) => {
        if (option) {
            if (return_objects) {
                setValue(name, option);
            } else if (return_array) {
                setValue(name, option.map((i) => i.value));
            } else {
                setValue(name, multi ? option.map((i) => i.value).join(",") : option.value);
            }
        } else {
            setValue(name, multi ? [] : null);
        }
    };

    if (hide_when_no_options && !options?.length) {
        return null;
    }

    const customStyles = useMemo(() => ({
        singleValue: (base, props) => ({
            ...base,
            opacity: props.data.__missing ? 0.8 : 1,
        }),
        multiValueLabel: (base, props) => ({
            ...base,
            opacity: props.data.__missing ? 0.8 : 1,
        }),
        menuPortal: (base) => ({ ...base, zIndex: 99999 })
    }), []);


    return (
        <FormGroup>
            {label && <Label>{label}</Label>}

            <Select
                id={name}
                options={options}
                isMulti={multi}
                hideSelectedOptions={false}
                isClearable={true}
                value={selectedValue}
                onChange={onChange}
                isDisabled={disabled}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={customStyles as any}
                placeholder={placeholder || undefined}
            />

            {description && <FormText className="d-block">{description}</FormText>}
        </FormGroup>
    );
};

export interface RadioButtonManagedProps extends SharedProps {
    post_value: string | number | string[] | null;
    default_value?: string;
}

export const RadioButtonManaged = ({
    setValue,
    object,
    post_value,
    name,
    label,
    disabled,
    options_name,
    description,
    default_value,
    extra_context = {}
}: RadioButtonManagedProps) => {
    const options = useManagedOptionsHook({ object: { ...object, ...extra_context }, name: options_name || name });
    const sortedOptions = useMemo(() => [...options].sort((a, b) => a.label.localeCompare(b.label, undefined, {
        numeric: true
    })), [options]);


    const selectedValue = useMemo(
        () => options.find((i) => String(i.value) === String(post_value || default_value)) || { value: undefined },
        [options, post_value, default_value]
    );

    return (
        <FormGroup>
            {label && <Label>{label}</Label>}

            <div>
                {options.length ? (
                    <RadioButtons
                        color="secondary"
                        options={sortedOptions}
                        name={name}
                        current={selectedValue.value}
                        onChange={setValue}
                        disabled={disabled}
                        className="me-1 mb-1"
                    />
                ) : (
                    <div className="text-warning">No options configured for {options_name || name}</div>
                )}
            </div>

            {description && <FormText className="d-block">{description}</FormText>}
        </FormGroup>
    );
};
